import useSpaceStore from '../../stores/spaceStore';
import { SpaceSettings, UISpace } from '../../types/spaces';
import { AnyWindowEntity } from '../../types/windows';
import { CRDTServiceWS, getCRDTServiceInstance } from './crdtServiceWS';
import { createFileLogger } from '../../utils/logger';
import * as Y from '../../shared/yjs';
import { getWsIdentitySub } from '../../api/config';

const log = createFileLogger('SPACE_CRDT_SERVICE', { enabled: false });

/**
 * Sort a raw tab array by an explicit tabOrder array.
 * Tabs missing from tabOrder (written by older clients) are appended at the end
 * so existing users see a graceful migration — their tabs stay visible even if
 * the order wasn't persisted yet.
 */
function sortTabsByOrder(rawTabs: any[], tabOrder: string[] | undefined): any[] {
  if (!tabOrder || tabOrder.length === 0) return rawTabs;
  const byId = new Map(rawTabs.map(t => [t.id, t]));
  const ordered = tabOrder.map(id => byId.get(id)).filter((t): t is any => !!t);
  const orderedIds = new Set(tabOrder);
  const remaining = rawTabs.filter(t => !orderedIds.has(t.id));
  return [...ordered, ...remaining];
}

// Create a single CRDT service instance
const crdtServiceWS = getCRDTServiceInstance();

export interface SpaceUpdate {
  activeWindowId?: string;
  layout?: any;
  settings?: {
    isContentsVisible?: boolean;
    isChatVisible?: boolean;
    bgColor?: string;
    viewMode?: 'spatial' | 'focused' | 'gui2' | 'home' | 'canvas' | 'spatialCanvas';
    sidebar?: {
      isVisible?: boolean;
      width?: number;
      mode?: 'normal' | 'overlay';
    };
  };
}

export interface SpaceConnectionInfo {
  spaceId: string;
  connectionCount: number;
  isMultiTab: boolean;
  providerStatus?: string;
  lastUpdate: number;
}

/**
 * Per-user Space SESSION state: settings (viewMode, sidebar, bgColor,
 * windowTabOrder, ...), the spatial-canvas camera, and chat-tab layout — all
 * scoped to `space:{spaceId}:{userId}`, one Y.Doc per (space, user) pair,
 * invisible to every other user.
 *
 * WINDOW layout (position, size, content, tabs, z-order) is NOT owned here —
 * see `sharedSpaceWindowsCRDTService.ts`, which owns the SHARED
 * `space-windows:{spaceId}` room every collaborator with access to the Space
 * sees and writes to. This split is what makes multiplayer possible while
 * keeping per-user preferences (which viewMode you're in, your own camera
 * position, your own chat-tab arrangement) independent per collaborator.
 */
export class SpaceCRDTService {
  private settingsListeners: Map<string, ((settings: Partial<SpaceSettings>) => void)[]> = new Map();
  initializedSpaces = new Set<string>();
  // Full CRDT key captured at initialization time so disconnectAll can find
  // the right provider even if getWsIdentitySub() returns null at logout.
  private initializedSpaceKeys = new Map<string, { spaceKey: string }>();
  private connectionListeners: Map<string, ((connectionInfo: SpaceConnectionInfo) => void)[]> = new Map(); // Track connection listeners
  private connectionPollIntervals: Map<string, ReturnType<typeof setInterval>> = new Map(); // One poller per spaceId
  private spaceObservers = new Map<string, () => void>(); // Track CRDT observers for cleanup
  // In-flight initializeSpace() calls, keyed by spaceId. A second caller for the
  // same space (fast remount, duplicate effect fire, etc.) awaits the FIRST
  // call's promise instead of racing it — see initializeSpace's guard below.
  // Without this, a concurrent call could observe the backend-seed transaction
  // mid-flight and misclassify not-yet-seeded settings keys as deleted (see the
  // doc.transact() wrapping in the seed branch for the other half of this fix).
  private initializingSpaces = new Map<string, Promise<void>>();
  private localOriginId = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; // Unique ID for this tab

  constructor(private crdt: CRDTServiceWS) {}

  onSpaceUpdate(spaceId: string, callback: (update: SpaceUpdate) => void) {
    return this.crdt.connect(this.getKey(spaceId), (update: any) => {
      if (update.data) callback(update.data);
    });
  }

  updateSettings(spaceId: string, settings: Partial<SpaceSettings>) {
    const spaceKey = this.getKey(spaceId);
    
    // ✅ NEW: Use Y.Map directly for proper CRDT sync
    const doc = this.crdt.getYDoc(spaceKey);
    if (doc) {
      doc.transact(() => {
        const settingsMap = doc.getMap('settings');
        Object.entries(settings).forEach(([key, value]) => {
          settingsMap.set(key, value);
        });
        log.info('[SpaceCRDT] ✅ Updated settings in CRDT Y.Map');
      }, this.localOriginId); // ✅ CRITICAL FIX: Pass origin to prevent same-tab observer loop
    } else {
      log.warn('[SpaceCRDT] ⚠️ No Y.Doc found, falling back to old update method');
      this.crdt.update(spaceKey, {
        settings,
      }, {
        updateType: 'update',
        preserveOtherConnections: true,
        preserveNoteConnections: true,
      });
    }
    
    const listeners = this.settingsListeners.get(spaceId) || [];
    listeners.forEach((listener) => listener(settings));
  }

  // ✅ FIXED: activeWindowId is per-tab/per-user, NOT collaborative data
  // Don't sync via CRDT - just save to backend
  updateActiveWindowId(spaceId: string, activeWindowId: string) {
    log.log('[SpaceCRDT] updateActiveWindowId - this is now a no-op (activeWindowId not in CRDT)');
    log.log('[SpaceCRDT] activeWindowId is per-tab and saved to backend only');
    // Backend saves happen automatically via backendSaveQueue in spaceStore
  }

  disconnectAll() {
    log.info('[SpaceCRDT] Disconnecting all');
    
    // Clean up all observers first
    this.spaceObservers.forEach((cleanup, spaceId) => {
      try {
        cleanup();
        log.info('[SpaceCRDT] Cleaned up observer for space:', spaceId);
      } catch (error) {
        log.warn('[SpaceCRDT] Error cleaning up observer:', error);
      }
    });
    this.spaceObservers.clear();
    
    this.settingsListeners.clear();

    // Disconnect all per-user session CRDT rooms using the keys captured at
    // initialization. Do NOT recompute via getKey here — that calls
    // getWsIdentitySub() which may return null after logout, producing a
    // wrong key that doesn't match the stored provider.
    const keysSnapshot = new Map(this.initializedSpaceKeys);
    this.initializedSpaces.clear();
    this.initializedSpaceKeys.clear();
    for (const [spaceId, { spaceKey }] of keysSnapshot) {
      try {
        this.crdt.disconnect(spaceKey);
      } catch (err) {
        log.warn(`[SpaceCRDT] Error disconnecting space ${spaceId}:`, err);
      }
    }
  }

  /**
   * Disconnects a specific tab's connection to a space.
   * This is safer for multi-tab scenarios than disconnectAll.
   */
  disconnectSpace(spaceId: string, tabId: string) {
    log.info(`[SpaceCRDT] 🧹 Disconnecting space ${spaceId} for tab ${tabId}`);
    
    try {
      // 1. Clean up the observer for this space
      const observerCleanup = this.spaceObservers.get(spaceId);
      if (observerCleanup) {
        log.info(`[SpaceCRDT] 🧹 Cleaning up observer for space: ${spaceId}`);
        observerCleanup();
        this.spaceObservers.delete(spaceId);
      }
      
      // 2. Remove from initialized spaces so it can be re-initialized
      this.initializedSpaces.delete(spaceId);
      log.info(`[SpaceCRDT] 🧹 Removed ${spaceId} from initialized spaces`);

      // 3. Clean up listeners
      this.settingsListeners.delete(spaceId);
      this.connectionListeners.delete(spaceId);

      // 4. Disconnect CRDT connection for this space using ONLY the key
      // captured at initialization. Re-invoking getKey() here would throw if
      // logout has already cleared the JWT; we'd rather skip the disconnect
      // entirely than risk constructing a legacy (shared, no-userId) room
      // name. A space cannot be in our initializedSpaces set without a
      // corresponding initializedSpaceKeys entry — initializeSpace writes
      // them together.
      const stored = this.initializedSpaceKeys.get(spaceId);
      this.initializedSpaceKeys.delete(spaceId);

      if (stored) {
        try {
          this.crdt.disconnect(stored.spaceKey);
          log.info(`[SpaceCRDT] 🧹 Disconnected CRDT for: ${stored.spaceKey}`);
        } catch (error) {
          log.warn(`[SpaceCRDT] Error disconnecting ${stored.spaceKey}:`, error);
        }
      } else {
        log.warn(`[SpaceCRDT] disconnectSpace: no captured key for ${spaceId} — skipping CRDT disconnect`);
      }

      // Presence is intentionally NOT touched here. It's owned entirely by
      // UserPresence's own mount-time connect()/unmount-time disconnect()
      // (keyed on the spaceId prop, see components/Space/UserPresence), which
      // is scoped to whichever space is actually being rendered right now.
      // `disconnectSpace` can now fire for a space far in the LRU tail — long
      // after the user navigated away from it — so calling the singleton
      // presence store's disconnect() here would wrongly tear down the
      // CURRENTLY active space's presence connection instead.

      log.info(`[SpaceCRDT] ✅ Successfully disconnected space ${spaceId}`);
    } catch (err) {
      log.warn(`[SpaceCRDT] Error disconnecting space ${spaceId} for tab ${tabId}:`, err);
    }
  }

  /**
   * Wait until `getWsIdentitySub()` returns a non-null value (i.e. the JWT
   * has been written to localStorage and parses cleanly) before proceeding.
   *
   * This is the single guard against the race that produces the "teammates
   * see each other's tabs" bug: on reload, React can mount the space page
   * and call `initializeSpace` before the auth pipeline has finished writing
   * tokens. Without this wait, `getKey` would throw — but we'd rather pause
   * for a few hundred milliseconds and proceed with the correct per-user
   * key than fail the entire space load and leave the user stuck.
   *
   * Polls every 50ms; throws after `timeoutMs`. JWT availability is normally
   * <100ms after page load, so 5s is generous.
   */
  private async waitForUserId(timeoutMs = 5000): Promise<string> {
    const immediate = getWsIdentitySub();
    if (immediate) return immediate;

    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      await new Promise<void>((resolve) => setTimeout(resolve, 50));
      const userId = getWsIdentitySub();
      if (userId) {
        log.info('[SpaceCRDT] waitForUserId resolved after', Date.now() - start, 'ms');
        return userId;
      }
    }
    throw new Error(
      `[SpaceCRDT] waitForUserId timed out after ${timeoutMs}ms — initializeSpace ` +
      'cannot proceed without an authenticated userId. The space room is per-user; ' +
      'continuing would risk constructing a shared/legacy room name.',
    );
  }

  async initializeSpace(spaceId: string, initialState: UISpace): Promise<void> {
    // Reentrancy guard: if a call for this exact space is already in flight
    // (fast remount, duplicate effect fire, etc.), await THAT promise instead
    // of starting a second, racing initialization.
    const existing = this.initializingSpaces.get(spaceId);
    if (existing) {
      log.info('[SpaceCRDT] initializeSpace already in flight for %s — awaiting it instead of racing', spaceId);
      return existing;
    }

    const promise = this._initializeSpaceInternal(spaceId, initialState).finally(() => {
      this.initializingSpaces.delete(spaceId);
    });
    this.initializingSpaces.set(spaceId, promise);
    return promise;
  }

  private async _initializeSpaceInternal(spaceId: string, initialState: UISpace): Promise<void> {
    // Warm reactivation: this space was never disconnected across the last
    // switch-away (desktopWarmCache kept it inside the LRU window), so its
    // Y.Doc has been merging remote CRDT updates in the background the whole
    // time — see the SpaceMismatch guard in setupSpaceObserver, which is what
    // silently drops those updates from mirroring into the store while this
    // space isn't active. Reactivating it is just a synchronous read of the
    // doc's current state; no network/IDB wait is needed.
    if (this.initializedSpaces.has(spaceId)) {
      const spaceKey = this.getKey(spaceId);
      const doc = this.crdt.getYDoc(spaceKey);
      if (doc) {
        log.log('[SpaceCRDT] ♻️ Reactivating warm space (no network wait):', spaceId);
        this._projectDocToStore(doc, initialState);
        return;
      }
      // Marked initialized but the doc is gone — stale bookkeeping (should not
      // normally happen since disconnectSpace always clears both together).
      // Fall through and re-initialize from scratch rather than getting stuck.
      log.warn('[SpaceCRDT] initializedSpaces had stale entry with no doc, re-initializing:', spaceId);
      this.initializedSpaces.delete(spaceId);
    }

    // CRITICAL: Block until we have a real userId. Constructing a per-user
    // room name with a null userId would either throw inside getKey() or, in
    // the historical buggy version, silently fall back to a suffix-less name
    // that the WebSocket server treated as a shared no-auth room — the cause
    // of the "users in the same org see each other's tabs" leak.
    await this.waitForUserId();

    const spaceKey = this.getKey(spaceId);
    
    log.log('[SpaceCRDT] 🚀 initializeSpace CALLED for:', spaceId);
    
    log.info('[SpaceCRDT] 🚀 Initializing space:', { spaceId });
    
    // Clean up any existing observer first (like docsCRDT does)
    const existingObserverCleanup = this.spaceObservers.get(spaceId);
    if (existingObserverCleanup) {
      console.error('[SpaceCRDT] ⚠️⚠️⚠️ Cleaning up existing observer - this indicates duplicate initialization!');
      existingObserverCleanup();
      this.spaceObservers.delete(spaceId);
    }
    
    // Capture full CRDT key now while userId is available. disconnectAll
    // and friends must NEVER recompute this via getWsIdentitySub() — that
    // can return null mid-logout, which would either throw or (historically)
    // produce a shared, no-userId legacy key.
    this.initializedSpaceKeys.set(spaceId, { spaceKey });
    this.initializedSpaces.add(spaceId);
    
    // ✅ STEP 1: Get or create Y.Doc (like Docs does)
    const doc = this.crdt.ensureInitialized(spaceKey);
    const provider = this.crdt.getProvider(spaceKey);
    
    // ✅ Wait for IndexedDB to finish loading its persisted state before reading
    // the settings map. Without this wait, the map is empty at init time even
    // when IDB has data, because IDB loads asynchronously after ensureInitialized.
    await this.crdt.waitForIdbSynced(spaceKey);
    
    log.info('[SpaceCRDT] Y.Doc and provider ready:', {
      hasDoc: !!doc,
      hasProvider: !!provider,
      providerConnected: provider?.wsconnected,
      providerSynced: provider?.synced
    });
    
    // ✅ STEP 2: Check if CRDT room already has settings (multi-tab / returning user scenario)
    const settingsMap = doc.getMap('settings');
    const crdtHasData = settingsMap.size > 0;
    
    log.info('[SpaceCRDT] 🔍 CRDT room state check:', {
      crdtHasData,
      crdtSettingsKeys: Array.from(settingsMap.keys()),
      providerSynced: provider?.synced
    });
    
    // ✅ STEP 3: Decide - are we joining an existing room or creating it?
    // Only treat the room as "new" once we have confirmed the server state via WS sync.
    // If the provider hasn't synced yet we cannot know whether the server has settings;
    // seeding (and clearing) from backend REST before sync can overwrite other tabs' state.
    if (crdtHasData || !provider?.synced) {
      log.info('[SpaceCRDT] 🤝 Using existing CRDT state (crdtHasData=%s, providerSynced=%s)', crdtHasData, provider?.synced);
      this._projectDocToStore(doc, initialState);
    } else {
      // WS has synced and the server-side CRDT is confirmed empty — seed from backend REST.
      // This is the "first ever open" case (new space or first device opening this space).
      log.info('[SpaceCRDT] 🎬 CRDT is empty after WS sync — seeding settings from backend REST state');
      
      doc.transact(() => {
        settingsMap.clear();
        if (initialState.settings) {
          Object.entries(initialState.settings).forEach(([key, value]) => {
            settingsMap.set(key, value);
          });
        }
      }, this.localOriginId);
      
      // Also update the store directly — the observer skips transactions with
      // localOriginId, so the seed write above won't reach the store via the
      // normal observer path. `windows` is deliberately excluded from this
      // patch (and preserved as-is via the spread order below) — that's the
      // shared window service's projection to own, not this settings-only
      // service's; overwriting it here with `initialState.windows` (the
      // largely-vestigial backend REST field) would race whichever value
      // `sharedSpaceWindowsCRDTService.initializeSpace` has already written
      // or is about to write.
      useSpaceStore.setState((state) => ({
        activeSpace: {
          ...initialState,
          windows: state.activeSpace?.windows ?? {},
        }
      }));
      
      log.info('[SpaceCRDT] ✅ Initialized CRDT with backend settings state');
    }
    
    // ✅ STEP 4: Set up CRDT observer for continuous sync (THE MISSING PIECE!)
    this.setupSpaceObserver(spaceId, doc);
    
    log.info('[SpaceCRDT] ✅ Space initialization complete - bidirectional settings sync active');
  }

  /**
   * Wait until the space's WebSocket room has completed a full sync round-trip
   * with the server (not just loaded IndexedDB).
   */
  async waitForSpaceSynced(spaceId: string, timeoutMs?: number): Promise<boolean> {
    const spaceKey = this.getKey(spaceId);
    return this.crdt.waitForSynced(spaceKey, timeoutMs);
  }

  /**
   * Set up CRDT observer for bidirectional settings sync (like Docs does).
   */
  private setupSpaceObserver(spaceId: string, doc: Y.Doc) {
    const spaceKey = this.getKey(spaceId);

    // Clean up any existing observer first
    const existingCleanup = this.spaceObservers.get(spaceId);
    if (existingCleanup) {
      console.error('[SpaceCRDT] ⚠️⚠️⚠️ DUPLICATE OBSERVER SETUP DETECTED FOR:', spaceId);
      console.error('[SpaceCRDT] This will cause duplicate updates! Cleaning up old observer first.');
      existingCleanup();
    }

    const settingsMap = doc.getMap('settings');

    // Track if we're currently updating to prevent loops
    let isUpdatingFromCRDT = false;

    const observer = (update: Uint8Array, origin: any) => {
      if (isUpdatingFromCRDT) {
        return;
      }

      if (origin === this.localOriginId) {
        return;
      }

      // 🛡️ Defensive guard: if the active space has changed since this
      // observer was attached, ignore the event. The cleanup path calls
      // doc.off(...) synchronously, but a Y.Doc transaction that started
      // before the off() can still iterate observers — applying that update
      // would write the OLD space's CRDT state into the NEW space's store
      // and cause cross-space tab leakage. The closure spaceId is the
      // canonical owner; the active store space is the one we're allowed
      // to mutate.
      const activeSpaceId = useSpaceStore.getState().activeSpace?.id;
      if (activeSpaceId && activeSpaceId !== spaceId) {
        // eslint-disable-next-line no-console
        console.warn('[SpaceCRDT][SpaceMismatch] setupSpaceObserver fire for stale space — ignoring', {
          observerSpaceId: spaceId,
          activeSpaceId,
        });
        return;
      }

      try {
        isUpdatingFromCRDT = true;

        const settingsFromCRDT: any = {};
        settingsMap.forEach((value: any, key: string) => {
          settingsFromCRDT[key] = value;
        });

        // Strip per-user navigation fields from shared CRDT settings before
        // applying to local store.
        if (settingsFromCRDT.spaceChat) {
          const { tabs: _t, activeTabId: _a, currentThreadIds: _c, ...sharedSpaceChat } = settingsFromCRDT.spaceChat;
          settingsFromCRDT.spaceChat = sharedSpaceChat;
        }

        useSpaceStore.setState((state) => ({
          activeSpace: {
            ...(state.activeSpace || {}),
            // Merge CRDT settings ON TOP of existing settings so that fields
            // only stored in the backend (e.g. background) are not wiped when
            // the CRDT fires with a partial settings snapshot (e.g. only viewMode).
            settings: Object.keys(settingsFromCRDT).length > 0
              ? { ...(state.activeSpace?.settings || {}), ...settingsFromCRDT }
              : state.activeSpace?.settings,
          } as any
        }));
      } finally {
        isUpdatingFromCRDT = false;
      }
    };

    // Add observer to Y.Doc
    doc.on('update', observer);

    // Store cleanup function
    this.spaceObservers.set(spaceId, () => {
      doc.off('update', observer);
      log.info('[SpaceCRDT] 🧹 Removed observer for space:', spaceId);
    });

    log.info(`[SpaceCRDT] ✅ CRDT observer set up for bidirectional settings sync (room=${spaceKey})`);
  }

  /**
   * Deserialize a space's Y.Doc `settings` map and write it into spaceStore
   * as the active projection. Shared by the cold-load "join an existing CRDT
   * room" branch of initializeSpace and by warm reactivation (revisiting a
   * space whose Y.Doc was kept alive in the background) — both cases are the
   * same operation: "the doc already has the authoritative settings state,
   * just read it into the store synchronously".
   */
  private _projectDocToStore(doc: Y.Doc, initialState: UISpace): void {
    const settingsMap = doc.getMap('settings');

    const settingsFromCRDT: any = {};
    settingsMap.forEach((value: any, key: string) => {
      settingsFromCRDT[key] = value;
    });

    // Strip per-user navigation fields — same guard as setupSpaceObserver.
    if (settingsFromCRDT.spaceChat) {
      const { tabs: _t, activeTabId: _a, currentThreadIds: _c, ...sharedSpaceChat } = settingsFromCRDT.spaceChat;
      settingsFromCRDT.spaceChat = sharedSpaceChat;
    }

    // DON'T update activeWindowId here - it's per-tab, restored from localStorage
    // by spaceStore.initialize() after this resolves. DON'T overwrite `windows`
    // with `initialState.windows` here either — see the seed branch above for
    // why; preserve whatever the shared window service has already projected.
    useSpaceStore.setState((state) => ({
      activeSpace: {
        ...initialState,
        windows: state.activeSpace?.windows ?? {},
        settings: Object.keys(settingsFromCRDT).length > 0 ? settingsFromCRDT : initialState.settings,
      }
    }));
  }

  async updateSpaceName(spaceId: string, name: string) {
    log.info('[SpaceCRDT] Updating name:', { spaceId, name });
    const key = this.getKey(spaceId);
    await this.crdt.update(key, {
      data: {
        name
      }
    }, {
      updateType: 'update',
      preserveOtherConnections: true,
      preserveNoteConnections: true,
    });
  }

  /**
   * Persist the spatial canvas camera (pan + zoom) to the per-user space Y.Doc's
   * 'meta' Y.Map. This keeps the viewport position across reloads and devices.
   */
  saveCameraToMeta(spaceId: string, camera: { pan: { x: number; y: number }; zoom: number }) {
    try {
      const spaceKey = this.getKey(spaceId);
      const doc = this.crdt.getYDoc(spaceKey);
      if (!doc) return;
      doc.transact(() => {
        const metaMap = doc.getMap('meta');
        metaMap.set('camera', camera);
      }, this.localOriginId);
      log.info('[SpaceCRDT] Saved camera to meta:', spaceId, camera);
    } catch (e) {
      log.error('[SpaceCRDT] Failed to save camera:', e);
    }
  }

  /**
   * Load the spatial canvas camera from the per-user space Y.Doc 'meta' map.
   * Returns null if no camera has been persisted yet.
   */
  loadCameraFromMeta(spaceId: string): { pan: { x: number; y: number }; zoom: number } | null {
    try {
      const spaceKey = this.getKey(spaceId);
      const doc = this.crdt.getYDoc(spaceKey);
      if (!doc) return null;
      const metaMap = doc.getMap('meta');
      const camera = metaMap.get('camera') as any;
      if (camera?.pan && typeof camera.zoom === 'number') return camera;
    } catch {
      // Non-fatal
    }
    return null;
  }

  /**
   * Read this user's pre-existing per-user window layout for this space, if
   * any Y.Doc for it has already been opened (see initializeSpace's own
   * ensureInitialized call — settings and this legacy read share the same
   * per-user doc, so this is a zero-extra-connection lookup).
   *
   * ONLY used as the migration seed source for
   * `sharedSpaceWindowsCRDTService.initializeSpace` the first time a Space's
   * shared window room is opened (i.e. the shared room is confirmed empty).
   * This is a narrow, explicit, read-only bridge between the old per-user
   * window storage and the new shared one — it does not resurrect any of the
   * write/observe/delete logic that used to live here for windows.
   */
  readLegacyWindowsForMigration(spaceId: string): Record<string, AnyWindowEntity> | null {
    const spaceKey = this.getKey(spaceId);
    const doc = this.crdt.getYDoc(spaceKey);
    if (!doc) return null;
    const windowsMap = doc.getMap('windows');
    if (windowsMap.size === 0) return null;
    const windows: Record<string, any> = {};
    windowsMap.forEach((win: any, windowId: string) => {
      try {
        windows[windowId] = win?.toJSON ? win.toJSON() : JSON.parse(JSON.stringify(win));
      } catch (e) {
        log.error('[SpaceCRDT] Failed to deserialize legacy window during migration read:', windowId, e);
      }
    });
    return windows;
  }

  // Add a method to get space data directly from CRDT
  async getSpace(spaceId: string): Promise<UISpace | null> {
    try {
      const key = this.getKey(spaceId);
      const data = await this.crdt.get(key);
      // Need to handle the Doc type correctly
      return data as unknown as UISpace;
    } catch (e) {
      log.error('[SpaceCRDT] Error getting space:', e);
      return null;
    }
  }

  /**
   * Per-user Y.Doc key for space SESSION state (settings, camera, legacy
   * window layout retained only for migration reads). Scoping by userId
   * ensures this state is shared only across the same user's browser
   * tabs/windows — never leaked to other users who happen to have the same
   * org space open.
   *
   * If `getWsIdentitySub()` is null at call time we throw rather than fall
   * back to a suffix-less `space:{spaceId}` name. The suffix-less form was
   * the legacy room shape that bypassed JWT auth on the server and silently
   * CRDT-merged every racing user into a single shared room — the historical
   * cause of "users in the same org are seeing each other's tabs". Callers
   * are expected to wait for auth via `waitForUserId` before constructing a
   * key (see `initializeSpace`).
   */
  private getKey(spaceId: string) {
    const userId = getWsIdentitySub();
    if (!userId) {
      throw new Error(
        '[SpaceCRDT] getKey called with no authenticated userId — refusing to ' +
        'construct a suffix-less space room name. Wait for auth before subscribing.',
      );
    }
    return `space:${spaceId}:${userId}`;
  }

  /**
   * Per-user Y.Doc key for chat tabs. Using a user-scoped key ensures that
   * tab state is shared only across the same user's browser tabs/windows —
   * never leaked to other users who happen to be in the same space.
   */
  private getChatTabsKey(spaceId: string, userId: string) {
    return `space-chat:${spaceId}:${userId}`;
  }

  /**
   * Write chat tabs to a per-user Y.Doc.
   *
   * The document is keyed as `space-chat:{spaceId}:{userId}` so that each
   * user's tab state is isolated. Changes replicate only to the same user's
   * other browser tabs/windows — never to a different user in the same space.
   *
   * Uses an explicit per-key diff instead of clear()+set(). Y.Map.clear() only
   * deletes keys visible locally at transaction time — keys that exist only on
   * the server (or in another window that hasn't synced yet) are not removed,
   * so they reappear on the next WS merge. Explicit delete() ops propagate as
   * real CRDT tombstones and prevent ghost-tab resurrection.
   */
  writeChatTabs(spaceId: string, userId: string, tabs: any[], activeTabId: string | null): void {
    const key = this.getChatTabsKey(spaceId, userId);
    const doc = this.crdt.ensureInitialized(key);
    const desiredIds = new Set(tabs.map(t => t.id));
    doc.transact(() => {
      const chatTabsMap = doc.getMap<any>('chatTabs');
      const chatMetaMap = doc.getMap<any>('chatMeta');
      // Delete any key present in the map that is not in the desired set.
      // Collecting first avoids mutating the map during iteration.
      const keysToDelete: string[] = [];
      chatTabsMap.forEach((_: any, k: string) => {
        if (!desiredIds.has(k)) keysToDelete.push(k);
      });
      for (const k of keysToDelete) chatTabsMap.delete(k);
      // Upsert each desired tab.
      for (const tab of tabs) {
        chatTabsMap.set(tab.id, tab);
      }
      chatMetaMap.set('activeTabId', activeTabId ?? '');
      // Persist explicit tab order — Y.Map iteration order is not guaranteed,
      // so we store a separate ordered array to reconstruct the correct sequence.
      chatMetaMap.set('tabOrder', tabs.map(t => t.id));
    }, this.localOriginId);
  }

  /**
   * Wait for both the IndexedDB and the WebSocket initial sync for the
   * per-user chat-tabs doc. Resolves as soon as both are done, or after a
   * 2-second timeout (offline / WebSocket unavailable).
   *
   * Use this in initialize() so tab validation sees the fully-merged state
   * (IDB + server) rather than just local IDB, which prevents ghost tabs
   * from reappearing via the post-IDB WebSocket merge.
   */
  async waitForChatTabsSync(spaceId: string, userId: string): Promise<void> {
    const key = this.getChatTabsKey(spaceId, userId);
    // waitForIdbSynced also calls ensureInitialized, which creates the doc and
    // attaches IDB. When IDB fires 'synced', the WS provider is connected.
    await this.crdt.waitForIdbSynced(key);
    const provider = this.crdt.getProvider(key);
    // No provider (unlikely) or already synced — nothing more to wait for.
    if (!provider || (provider as any).synced) return;
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, 2000);
      provider.once('sync', () => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  /**
   * Read current chat tabs from the per-user Y.Doc.
   * Returns null when the Y.Doc is not yet initialized or has no tabs stored.
   */
  readChatTabs(spaceId: string, userId: string): { tabs: any[]; activeTabId: string | null } | null {
    const key = this.getChatTabsKey(spaceId, userId);
    const doc = this.crdt.getYDoc(key);
    if (!doc) return null;
    const chatTabsMap = doc.getMap<any>('chatTabs');
    if (chatTabsMap.size === 0) return null;
    const rawTabs: any[] = [];
    chatTabsMap.forEach((tab: any) => {
      rawTabs.push(tab?.toJSON ? tab.toJSON() : { ...tab });
    });
    const chatMetaMap = doc.getMap<any>('chatMeta');
    const rawActiveTabId = chatMetaMap.get('activeTabId');
    const tabOrder: string[] | undefined = chatMetaMap.get('tabOrder');
    // Sort tabs by the stored order. Tabs missing from tabOrder (e.g. created on an
    // older client that didn't write tabOrder) are appended at the end.
    const tabs = sortTabsByOrder(rawTabs, tabOrder);
    return { tabs, activeTabId: rawActiveTabId || null };
  }


  /**
   * Subscribe to chat-tab changes from the same user's other browser tabs.
   * The callback fires only for remote writes (not for writes from this tab).
   * Returns a cleanup function.
   */
  observeChatTabs(
    spaceId: string,
    userId: string,
    callback: (tabs: any[], activeTabId: string | null) => void,
  ): () => void {
    const key = this.getChatTabsKey(spaceId, userId);
    const doc = this.crdt.ensureInitialized(key);
    const chatTabsMap = doc.getMap<any>('chatTabs');
    const chatMetaMap = doc.getMap<any>('chatMeta');

    const emit = (_event: any, transaction: Y.Transaction) => {
      if (transaction.origin === this.localOriginId) return;
      const rawTabs: any[] = [];
      chatTabsMap.forEach((tab: any) => {
        rawTabs.push(tab?.toJSON ? tab.toJSON() : { ...tab });
      });
      const rawActiveTabId = chatMetaMap.get('activeTabId');
      const tabOrder: string[] | undefined = chatMetaMap.get('tabOrder');
      const tabs = sortTabsByOrder(rawTabs, tabOrder);
      callback(tabs, rawActiveTabId || null);
    };

    chatTabsMap.observe(emit);
    chatMetaMap.observe(emit);
    return () => {
      chatTabsMap.unobserve(emit);
      chatMetaMap.unobserve(emit);
    };
  }

  /**
   * Get connection information for a space's per-user session room.
   *
   * Uses the key captured at `initializeSpace` time. If the space hasn't
   * been initialized yet (or has been disconnected), returns a "disconnected"
   * snapshot rather than re-deriving the per-user key — that would either
   * throw (if logged out) or, historically, silently produce the legacy
   * shared room name.
   */
  getSpaceConnectionInfo(spaceId: string): SpaceConnectionInfo {
    const stored = this.initializedSpaceKeys.get(spaceId);
    if (!stored) {
      return {
        spaceId,
        connectionCount: 0,
        isMultiTab: false,
        providerStatus: 'disconnected',
        lastUpdate: Date.now(),
      };
    }

    let connectionCount = 0;
    let providerStatus = 'disconnected';

    const spaceProvider = this.crdt.getProvider(stored.spaceKey);
    if (spaceProvider) {
      connectionCount++;
      providerStatus = spaceProvider.wsconnected ? 'connected' : 'connecting';
    }

    return {
      spaceId,
      connectionCount,
      isMultiTab: connectionCount > 1,
      providerStatus,
      lastUpdate: Date.now(),
    };
  }

  /**
   * Get detailed debug information about CRDT connections. Uses the captured
   * key and is safe to call before init / after disconnect.
   */
  getDetailedDebugInfo(spaceId: string) {
    const stored = this.initializedSpaceKeys.get(spaceId);
    if (!stored) {
      return {
        spaceId,
        keys: { space: null },
        providers: { space: null },
        connectionCount: 0,
        timestamp: new Date().toISOString(),
      };
    }

    const spaceProvider = this.crdt.getProvider(stored.spaceKey);

    return {
      spaceId,
      keys: {
        space: stored.spaceKey,
      },
      providers: {
        space: spaceProvider ? {
          connected: spaceProvider.wsconnected,
          room: spaceProvider.roomname,
          url: spaceProvider.url,
          status: spaceProvider.wsconnected ? 'connected' : 'disconnected',
        } : null,
      },
      connectionCount: spaceProvider ? 1 : 0,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Subscribe to connection changes for a space. A single 2-second poller is
   * shared across all subscribers for a given spaceId; it's started on the
   * first subscribe and stopped on the last unsubscribe. Previously each
   * subscriber spun up its own interval, which orphaned timers whenever
   * listeners were removed out of order.
   */
  onConnectionChange(spaceId: string, callback: (connectionInfo: SpaceConnectionInfo) => void): () => void {
    if (!this.connectionListeners.has(spaceId)) {
      this.connectionListeners.set(spaceId, []);
    }

    const listeners = this.connectionListeners.get(spaceId)!;
    listeners.push(callback);

    callback(this.getSpaceConnectionInfo(spaceId));

    if (!this.connectionPollIntervals.has(spaceId)) {
      const interval = setInterval(() => {
        const currentListeners = this.connectionListeners.get(spaceId);
        if (!currentListeners || currentListeners.length === 0) return;
        const connectionInfo = this.getSpaceConnectionInfo(spaceId);
        for (const listener of currentListeners) {
          try {
            listener(connectionInfo);
          } catch (error) {
            log.error('[SpaceCRDT] Error in connection listener:', error);
          }
        }
      }, 2000);
      this.connectionPollIntervals.set(spaceId, interval);
    }

    return () => {
      const currentListeners = this.connectionListeners.get(spaceId);
      if (!currentListeners) return;
      const index = currentListeners.indexOf(callback);
      if (index > -1) {
        currentListeners.splice(index, 1);
      }

      if (currentListeners.length === 0) {
        this.connectionListeners.delete(spaceId);
        const interval = this.connectionPollIntervals.get(spaceId);
        if (interval) {
          clearInterval(interval);
          this.connectionPollIntervals.delete(spaceId);
        }
      }
    };
  }
}

// Only export one instance
export const spaceCRDTService = new SpaceCRDTService(crdtServiceWS);
